import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { useQuery } from 'react-apollo';

import CustomControl from '../../../../../components/global/CustomControl';
import Badge from '../../../../../components/global/Badge';
import PriceDisplay from '../../../../../components/global/PriceDisplay';
import getPrintPartnerShippingOptions from '../../../../cards/graphql/queries/getPrintPartnerShippingOptions';
import { getCheckoutPrintPartnerShippingOptionId } from '../../../redux/checkoutSelectors';
import { setPrintPartnerShippingOption } from '../../../redux/checkoutActions';
import useShippingOptionPrices from './hooks/useShippingOptionPrices';

const STANDARD_SHIPPING_OPTION_ID = 'standard';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  addressId: PropTypes.string,
  checkoutItems: PropTypes.object,
  printPartner: PropTypes.shape({
    id: PropTypes.string,
  }),
  vendor: PropTypes.shape({
    id: PropTypes.string,
  }),
  selectedShippingOptionId: PropTypes.string,
  onShippingOptionChange: PropTypes.func.isRequired,
};

const CheckoutCartShippingOptions = ({
  account,
  addressId,
  checkoutItems,
  printPartner,
  vendor,
  selectedShippingOptionId,
  onShippingOptionChange,
}) => {
  const { data: { printPartners } = {}, loading: loadingShippingOptions } =
    useQuery(getPrintPartnerShippingOptions, {
      variables: {
        print_partner_id: printPartner?.id,
        vendor_account_id: vendor?.id,
      },
      skip: !printPartner?.id,
      fetchPolicy: 'cache-first',
    });
  const shippingOptions =
    printPartners?.records?.[0]?.shipping_options?.records ?? [];

  const { pricedShippingOptions, loading: loadingPrices } =
    useShippingOptionPrices({
      account,
      addressId,
      checkoutItems,
      shippingOptions,
    });
  const loading = loadingShippingOptions || loadingPrices;

  useEffect(() => {
    // Drop a pick that is no longer offered, e.g. the cart changed.
    if (
      !loading &&
      selectedShippingOptionId &&
      !pricedShippingOptions.some(({ id }) => id === selectedShippingOptionId)
    ) {
      onShippingOptionChange(null);
    }
  }, [loading, pricedShippingOptions, selectedShippingOptionId]);

  if (!pricedShippingOptions.length) {
    return null;
  }

  const options = [
    {
      id: STANDARD_SHIPPING_OPTION_ID,
      name: 'Standard Shipping',
    },
    ...pricedShippingOptions,
  ];

  return (
    <div className="mb-4">
      <h3 className="text-extra-bold text-faded">Shipping Option</h3>
      <div className="rounded-box p-0">
        {options.map(option => {
          const isStandard = option.id === STANDARD_SHIPPING_OPTION_ID;
          const checked = isStandard
            ? !selectedShippingOptionId
            : selectedShippingOptionId === option.id;
          const classes = classNames({
            'checkout-cart-shipping-option': true,
            'checkout-cart-shipping-option-selected': checked,
          });

          return (
            <div className={classes} key={option.id}>
              <CustomControl
                type="radio"
                id={`checkout-shipping-option-${option.id}`}
                name="checkout-shipping-option"
                className="text-dark mb-0"
                checked={checked}
                onChange={() =>
                  onShippingOptionChange(isStandard ? null : option.id)
                }
                label={
                  <span className="checkout-cart-shipping-option-label">
                    <span className="text-bold">{option.name}</span>
                    {option.description && (
                      <span className="text-sm text-muted">
                        {option.description}
                      </span>
                    )}
                  </span>
                }
              />
              {!isStandard && (
                <Badge className="text-extra-bold ml-auto" badgeStyle="yellow">
                  <PriceDisplay
                    price={option.shipping_price.value}
                    currency={option.shipping_price.currency}
                  />
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

CheckoutCartShippingOptions.propTypes = propTypes;

const mapStateToProps = state => ({
  selectedShippingOptionId: getCheckoutPrintPartnerShippingOptionId(state),
});

const mapDispatchToProps = {
  onShippingOptionChange: setPrintPartnerShippingOption,
};

export { CheckoutCartShippingOptions as CheckoutCartShippingOptionsComponent };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CheckoutCartShippingOptions);
